import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import NotificationDropdown from '../shared/NotificationDropdown';
import DashboardSubHeader from '../dashboard/DashboardSubHeader';
import {
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  UserRound,
  Shield,
  GraduationCap,
  PackageCheck,
  Ticket,
  Building,
  Wallet,
  Briefcase,
  Receipt,
  ListChecks,
  Cog,
  User,
  Settings,
} from 'lucide-react';
import logo from '../../assets/pictures/logo.png';

const MENU_SECTIONS = [
  {
    items: [
      {
        path: '/dashboard',
        icon: LayoutDashboard,
        label: 'Dashboard',
        exact: true,
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-600',
      },
    ],
  },
  {
    title: 'SERVICES',
    accentColor: 'bg-fuchsia-600',
    titleColor:
      'bg-gradient-to-r from-fuchsia-600 to-purple-600 bg-clip-text text-transparent',
    items: [
      {
        path: '/dashboard/services/profile-assessment',
        icon: UserRound,
        label: 'Profile Assessment',
        iconBg: 'bg-purple-500/20',
        iconColor: 'text-purple-500',
      },
      {
        path: '/dashboard/services/preapplication-support',
        icon: Shield,
        label: 'Preapplication Support',
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-500',
      },
      {
        path: '/dashboard/services/apply-university',
        icon: GraduationCap,
        label: 'Apply University',
        iconBg: 'bg-green-500/20',
        iconColor: 'text-green-500',
      },
      {
        path: '/dashboard/services/visa-interview',
        icon: PackageCheck,
        label: 'Visa & Interview Supp...',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-500',
      },
      {
        path: '/dashboard/services/ticket-travel',
        icon: Ticket,
        label: 'Ticket & Travel Support',
        iconBg: 'bg-red-500/20',
        iconColor: 'text-red-500',
      },
      {
        path: '/dashboard/services/accommodation',
        icon: Building,
        label: 'Find Accomodation',
        iconBg: 'bg-cyan-500/20',
        iconColor: 'text-cyan-500',
      },
      {
        path: '/dashboard/services/education-loan',
        icon: Wallet,
        label: 'Education Loan',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-500',
      },
      {
        path: '/dashboard/services/jobs-abroad',
        icon: Briefcase,
        label: 'Find Jobs Abroad',
        iconBg: 'bg-indigo-500/20',
        iconColor: 'text-indigo-500',
      },
    ],
  },
  {
    title: 'MY ACCOUNT',
    accentColor: 'bg-fuchsia-600',
    titleColor:
      'bg-gradient-to-r from-fuchsia-600 to-purple-600 bg-clip-text text-transparent',
    items: [
      {
        path: '/dashboard/subscriptions',
        icon: Receipt,
        label: 'Subscriptions',
        iconBg: 'bg-fuchsia-500/20',
        iconColor: 'text-fuchsia-500',
      },
      {
        path: '/dashboard/track',
        icon: ListChecks,
        label: 'Progress Report',
        iconBg: 'bg-teal-500/20',
        iconColor: 'text-teal-500',
      },
      {
        path: '/dashboard/payments',
        icon: Wallet,
        label: 'Payment History',
        iconBg: 'bg-rose-500/20',
        iconColor: 'text-rose-500',
      },
      {
        path: '/dashboard/settings',
        icon: Cog,
        label: 'Settings',
        iconBg: 'bg-gray-500/20',
        iconColor: 'text-gray-500',
      },
    ],
  },
];

function ProfileDropdown({ user, logout }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-white/60 transition-all duration-200 focus:outline-none"
      >
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold ring-2 ring-white shadow-md flex-shrink-0">
          {user?.image ? (
            <img
              src={user.image}
              alt={initials}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
        <div className="hidden sm:block text-left leading-tight">
          <p className="text-sm font-semibold text-slate-800 max-w-[120px] truncate">
            {user?.firstName} {user?.lastName}
          </p>
          <p className="text-xs text-slate-500 max-w-[120px] truncate">
            {user?.email}
          </p>
        </div>
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* User Info Header */}
          <div className="px-4 py-3.5 border-b border-slate-100 bg-gradient-to-br from-sky-50 to-indigo-50">
            <p className="text-sm font-semibold text-slate-900">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {user?.email}
            </p>
          </div>

          {/* Menu Items */}
          <div className="p-1.5">
            <button
              onClick={() => {
                navigate('/dashboard/profile');
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-all duration-150 group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-sky-100 flex items-center justify-center transition-colors">
                <User className="w-4 h-4 text-slate-500 group-hover:text-sky-600" />
              </div>
              My Profile
            </button>

            <button
              onClick={() => {
                navigate('/dashboard/settings');
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:bg-sky-50 hover:text-sky-700 transition-all duration-150 group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-sky-100 flex items-center justify-center transition-colors">
                <Settings className="w-4 h-4 text-slate-500 group-hover:text-sky-600" />
              </div>
              Account Settings
            </button>
          </div>

          {/* Logout */}
          <div className="p-1.5 border-t border-slate-100">
            <button
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all duration-150 group"
            >
              <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                <LogOut className="w-4 h-4 text-red-500" />
              </div>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  return (
    <div className="h-screen flex bg-sky-50/80 overflow-hidden">
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50
        bg-white border-r border-slate-300/60
        shadow-xl shadow-slate-200/40 flex flex-col transition-all duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${collapsed ? 'w-20' : 'w-64'} lg:h-screen`}
      >
        {/* Logo */}
        <div
          className={`h-[64px] px-4 flex items-center border-b border-slate-100 bg-[#E0E7FF]
          ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          <Link to="/dashboard" className="flex items-center gap-3 ">
            <img src={logo} alt="Fly8" className="w-16 h-12 object-contain" />
            {!collapsed && (
              <span
                className="text-sm font-bold tracking-wide
                bg-gradient-to-r from-sky-700 to-blue-600
                bg-clip-text text-transparent"
              >
                Student Portal
              </span>
            )}
          </Link>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex w-8 h-8 items-center justify-center rounded-md hover:bg-slate-100"
          >
            <ChevronLeft
              className={`w-4 h-4 transition-transform ${
                collapsed ? 'rotate-180' : ''
              }`}
            />
          </button>

          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 flex flex-col justify-between px-3 py-4 overflow-y-auto scrollbar-hidden">
          <div className="space-y-6">
            {MENU_SECTIONS.map((section, sIdx) => (
              <div key={sIdx}>
                {/* Section Header */}
                {section.title && !collapsed && (
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div
                      className={`w-1 h-5 ${section.accentColor} rounded-full`}
                    />
                    <span
                      className={`text-lg font-extrabold ${section.titleColor}`}
                    >
                      {section.title}
                    </span>
                  </div>
                )}
                {section.title && collapsed && (
                  <div className="flex justify-center mb-3">
                    <div
                      className={`w-6 h-0.5 ${section.accentColor} rounded-full`}
                    />
                  </div>
                )}

                {/* Section Items */}
                <div>
                  {section.items.map(item => {
                    const Icon = item.icon;
                    const isActive = item.exact
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`relative overflow-hidden flex items-center gap-3 px-3 py-1 rounded-lg text-sm font-semibold transition-all duration-200
                          ${collapsed ? 'justify-center' : ''}
                          ${
                            isActive
                              ? 'bg-blue-900 text-white font-semibold'
                              : 'text-[#0DA2E7] font-semibold hover:text-white before:absolute before:inset-0 before:bg-blue-900 before:rounded-lg before:translate-x-full before:transition-transform before:duration-300 before:ease-out hover:before:translate-x-0 hover:shadow-md'
                          }`}
                        title={collapsed ? item.label : undefined}
                      >
                        <div
                          className={`relative z-10 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isActive ? 'bg-white/20' : item.iconBg
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 ${isActive ? 'text-white' : item.iconColor}`}
                          />
                        </div>
                        {!collapsed && (
                          <span
                            className={`relative z-10 truncate min-w-0 text-sm ${isActive ? 'text-white' : 'text-[#0DA2E7]'}`}
                          >
                            {item.label}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* User Section */}
          <div className="pt-4 border-t border-slate-200/60">
            {!collapsed && (
              <div className="flex items-center gap-3 mb-3 p-2.5 rounded-xl bg-gradient-to-r from-sky-50/80 to-indigo-50/60 border border-sky-100/50">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold ring-2 ring-white shadow-lg shadow-sky-500/25">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-blue-500/70 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            )}

            {collapsed && (
              <div className="flex justify-center mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold ring-2 ring-sky-100 shadow-lg shadow-sky-500/25">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
              </div>
            )}

            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className={`w-full flex items-center gap-2 rounded-xl border-red-200/60 text-red-500 bg-red-50/40 hover:bg-red-50 hover:text-red-600 hover:border-red-300 hover:shadow-sm transition-all duration-200
              ${collapsed ? 'justify-center px-2' : 'justify-start'}`}
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && 'Logout'}
            </Button>
          </div>
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-[64px] bg-[#E0E7FF] border-slate-300/60 shadow-slate-200/40  px-4 lg:px-8 flex items-center justify-between sticky top-0 z-30 shadow-lg ">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>

            <div>
              <h2 className="text-lg font-bold font-outfit bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent">
                Welcome back, {user?.firstName}
              </h2>
              <p className="text-xs text-slate-400 hidden sm:block">
                Track your study abroad journey
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationDropdown />
            <ProfileDropdown user={user} logout={logout} />
          </div>
        </header>

        {/* Sub-header Navigation */}
        <DashboardSubHeader />

        {/* Content */}
        <main className="flex-1 overflow-auto scrollbar-hidden p-4 lg:p-8 bg-sky-50/80">
          {children}
        </main>
      </div>
    </div>
  );
}
